// src/components/personal/sections/OrdersSection/utils/orderFilters.ts
import { Order, OrderFilters } from '../types/types';

/**
 * Создает объект с пустыми фильтрами по умолчанию
 */
export const getEmptyFilters = (): OrderFilters => ({
  dateFrom: '',
  dateTo: '',
  status: 'all',
  paymentStatus: 'all',
  platform: 'all',
  client: '',
  amountFrom: '',
  amountTo: ''
});

/**
 * Улучшенная функция фильтрации списка заказов
 * 
 * @param orders Исходный список заказов
 * @param filters Объект с параметрами фильтрации
 * @param searchQuery Строка поиска (ID, телефон)
 * @returns Отфильтрованный список заказов
 */
export const filterOrders = (
  orders: Order[],
  filters: OrderFilters,
  searchQuery: string
): Order[] => {
  // Для отладки
  console.log(`Фильтрация ${orders.length} заказов:`, { 
    filters: JSON.stringify(filters), 
    searchQuery 
  });
  
  // Оптимизация: если нет заказов, сразу возвращаем пустой массив
  if (!orders.length) return [];
  
  // Оптимизация: если нет фильтров и поиска, возвращаем исходный массив
  if (!isFilteringActive(filters, searchQuery)) {
    console.log('Фильтры не активны, возвращаем весь список');
    return [...orders];
  }
  
  // Очищаем поисковый запрос от форматирования для сравнения с телефоном
  const cleanedSearchQuery = searchQuery.replace(/[-\s()+]/g, '');
  
  const filteredOrders = orders.filter(order => {
    // Поиск только по ID заказа и телефону клиента
    if (searchQuery) {
      // Проверяем точное совпадение по ID
      const matchesId = order.id === searchQuery;
      
      // Очищаем телефон от символов для удобства поиска
      const cleanedPhone = order.client_phone ? order.client_phone.replace(/[-\s()+]/g, '') : '';
      
      // Проверяем совпадение по телефону
      const matchesPhone = cleanedPhone.includes(cleanedSearchQuery);
      
      // Если не соответствует ни ID, ни телефону - исключаем из результатов
      if (!(matchesId || matchesPhone)) {
        return false;
      }
    }
    
    // Фильтр по статусу заказа
    if (filters.status && filters.status !== 'all' && order.status !== filters.status) {
      return false;
    }
    
    // Фильтр по статусу оплаты
    if (filters.paymentStatus && filters.paymentStatus !== 'all') {
      const hasPaymentWithStatus = order.payments && order.payments.some(payment => payment.status === filters.paymentStatus);
      
      // Если выбран статус оплаты, но у заказа нет платежей с таким статусом
      if (!hasPaymentWithStatus) {
        return false;
      }
    }
    
    // Фильтр по платформе
    if (filters.platform && filters.platform !== 'all' && order.platform !== filters.platform) {
      return false;
    }
    
    // Фильтр по диапазону дат
    if (filters.dateFrom && order.created_at) {
      const orderDate = new Date(order.created_at);
      const fromDate = new Date(filters.dateFrom);
      fromDate.setHours(0, 0, 0, 0); // Начало дня
      
      if (orderDate < fromDate) {
        return false;
      }
    }
    
    if (filters.dateTo && order.created_at) {
      const orderDate = new Date(order.created_at);
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // Конец дня
      
      if (orderDate > toDate) {
        return false;
      }
    }
    
    // Фильтр по клиенту
    if (filters.client) {
      // Проверяем совпадение по ID клиента
      const matchesClientId = order.client === filters.client;
      
      // Проверяем совпадение по имени клиента (если есть)
      const matchesClientName = 
        order.client_name && 
        order.client_name.toLowerCase().includes(filters.client.toLowerCase());
      
      // Если не соответствует ни ID, ни имени клиента - исключаем из результатов
      if (!(matchesClientId || matchesClientName)) {
        return false;
      }
    }
    
    // Фильтр по сумме заказа (from)
    if (filters.amountFrom && filters.amountFrom.trim() !== '') {
      const amountFrom = parseFloat(filters.amountFrom);
      // Используем числовое значение суммы заказа
      const orderTotal = 
        typeof order.total_cost === 'string' 
          ? parseFloat(order.total_cost.replace(/[^\d.-]/g, '')) 
          : (order.cost_data?.total || 0);
      
      if (!isNaN(amountFrom) && orderTotal < amountFrom) {
        return false;
      }
    }
    
    // Фильтр по сумме заказа (to)
    if (filters.amountTo && filters.amountTo.trim() !== '') {
      const amountTo = parseFloat(filters.amountTo);
      // Используем числовое значение суммы заказа
      const orderTotal = 
        typeof order.total_cost === 'string' 
          ? parseFloat(order.total_cost.replace(/[^\d.-]/g, '')) 
          : (order.cost_data?.total || 0);
      
      if (!isNaN(amountTo) && orderTotal > amountTo) {
        return false;
      }
    }
    
    // Заказ прошел все проверки фильтров
    return true;
  });
  
  console.log(`После фильтрации осталось ${filteredOrders.length} заказов`);
  return filteredOrders;
};

/**
 * Сортирует список заказов по указанному полю и направлению
 * 
 * @param orders Исходный список заказов
 * @param column Поле для сортировки
 * @param direction Направление сортировки ('asc' или 'desc')
 * @returns Отсортированный список заказов
 */
export const sortOrders = (
  orders: Order[],
  column: string,
  direction: 'asc' | 'desc'
): Order[] => {
  // Оптимизация: если нет заказов, сразу возвращаем пустой массив
  if (!orders.length) return [];
  
  return [...orders].sort((a, b) => {
    let valueA: any = (a as any)[column];
    let valueB: any = (b as any)[column];
    
    // Специальная обработка для разных типов данных
    
    // Для строк - переводим в нижний регистр для корректного сравнения
    if (typeof valueA === 'string') {
      valueA = valueA.toLowerCase();
    }
    if (typeof valueB === 'string') {
      valueB = valueB.toLowerCase();
    }
    
    // Для числовых значений - преобразуем в числа
    if (column === 'total_cost') {
      valueA = typeof valueA === 'string' 
        ? parseFloat(valueA.replace(/[^\d.-]/g, '')) || 0 
        : (a.cost_data?.total || 0);
      
      valueB = typeof valueB === 'string' 
        ? parseFloat(valueB.replace(/[^\d.-]/g, '')) || 0 
        : (b.cost_data?.total || 0);
    }
    
    // Для дат - преобразуем в объекты Date
    if (column === 'created_at' || column === 'completed_date') {
      valueA = valueA ? new Date(valueA).getTime() : 0;
      valueB = valueB ? new Date(valueB).getTime() : 0;
    }
    
    // Сравнение значений с учетом направления сортировки
    if (valueA < valueB) {
      return direction === 'asc' ? -1 : 1;
    }
    if (valueA > valueB) {
      return direction === 'asc' ? 1 : -1;
    }
    return 0;
  });
};

/**
 * Применяет пагинацию к отфильтрованному списку заказов
 * 
 * @param orders Отфильтрованный список заказов
 * @param currentPage Текущая страница
 * @param pageSize Размер страницы
 * @returns Список заказов для текущей страницы
 */
export const paginateOrders = (
  orders: Order[],
  currentPage: number,
  pageSize: number
): Order[] => {
  // Оптимизация: если нет заказов, сразу возвращаем пустой массив
  if (!orders.length) return [];
  
  // Оптимизация: если размер страницы 0 или отрицательный, возвращаем пустой массив
  if (pageSize <= 0) return [];
  
  // Корректируем текущую страницу, если она некорректна
  const safeCurrentPage = Math.max(1, currentPage);
  const startIndex = (safeCurrentPage - 1) * pageSize;
  
  // Оптимизация: если стартовый индекс больше длины массива, возвращаем пустой массив
  if (startIndex >= orders.length) return [];
  
  const endIndex = Math.min(startIndex + pageSize, orders.length);
  return orders.slice(startIndex, endIndex);
};

/**
 * Определяет, активны ли фильтры или поиск
 * 
 * @param filters Объект с параметрами фильтрации
 * @param searchQuery Строка поиска
 * @returns True, если какой-либо фильтр активен
 */
export const isFilteringActive = (
  filters: OrderFilters,
  searchQuery: string
): boolean => {
  // Поиск активен, если строка поиска не пуста
  const hasSearchQuery = searchQuery && searchQuery.trim() !== '';
  
  // Проверяем наличие хотя бы одного активного фильтра
  const hasActiveFilters = Object.entries(filters).some(([key, value]) => {
    if (key === 'status' || key === 'paymentStatus' || key === 'platform') {
      return value !== 'all';
    }
    
    // Для числовых полей проверяем, что это не пустая строка и не строка с пробелами
    if (typeof value === 'string') {
      return value.trim() !== '';
    }
    
    return Boolean(value);
  });
  
  const result = hasSearchQuery || hasActiveFilters;
  console.log(`Фильтрация активна: ${result} (поиск: ${hasSearchQuery}, фильтры: ${hasActiveFilters})`);
  return result;
};